import React, { Component } from 'react';
import classNameBind from 'classnames/bind';
import scroll from 'scroll';
import Button from '../Button';
import { ResizeAware } from '../ResizeAware';

import styles from './TabsWithScroll.module.css';
import Tabs from './components/Tabs';

/**
 * Size of scrolled "page" of the tabs. It's calculated based on visible area of the tabs.
 * 1 - is scrolling 100% of visible tabs element width (part of it may be hidden by overflow)
 *
 * It's meant as a quick-fix for issue with cutting visible tab names before and after scroll.
 * Example:
 *
 * |[Tab 1][Tab 2][Tab 3][Ta|
 *
 * scroll right
 *
 * |b 4][Tab 5][Tab 6][Tab 7|
 */
const SCROLL_PAGE_PART = 0.8;
const bindClasses = classNameBind.bind(styles);

class TabsWithScroll extends Component {
  constructor() {
    super();
    this.tabsRef = React.createRef();
    this.state = {
      prevDisabled: true,
      nextDisabled: true,
    };
  }

  componentDidMount() {
    this.onResize();
  }

  getButtonsState = (scrollLeft) => {
    const tabs = this.tabsRef.current;

    return {
      prevDisabled: scrollLeft <= 0,
      nextDisabled: scrollLeft >= tabs.scrollWidth - tabs.clientWidth,
    };
  };

  onResize = () => {
    const tabs = this.tabsRef.current;

    if (tabs) {
      this.setState(this.getButtonsState(tabs.scrollLeft));
    }
  };

  onScroll = (sign) => {
    const tabs = this.tabsRef.current;

    if (tabs) {
      const newLeft = tabs.scrollLeft + sign * SCROLL_PAGE_PART * tabs.clientWidth;
      scroll.left(tabs, newLeft);
      this.setState(this.getButtonsState(newLeft));
    }
  };

  render() {
    const { withoutBorderBottom, segmented, ...rest } = this.props;
    const { prevDisabled, nextDisabled } = this.state;
    const showButtons = !(prevDisabled && nextDisabled);

    return (
      <div
        className={bindClasses('wrapper', { 'with-border-bottom': !withoutBorderBottom })}
        data-ppuc-id="TabsWithScroll"
      >
        {showButtons && (
          <div className={bindClasses('button', { prev: !prevDisabled, segmented })}>
            <Button
              appearance="subdued"
              disabled={prevDisabled}
              icon="chevron-left"
              onClick={() => this.onScroll(-1)}
            />
          </div>
        )}
        <ResizeAware
          wrapperStyles={{ position: 'relative', width: '100%', minWidth: '0' }}
          onResize={this.onResize}
        >
          <Tabs {...rest} segmented={segmented} setRef={this.tabsRef} />
        </ResizeAware>
        {showButtons && (
          <div className={bindClasses('button', { next: !nextDisabled, segmented })}>
            <Button
              appearance="subdued"
              disabled={nextDisabled}
              icon="chevron-right"
              onClick={() => this.onScroll(1)}
            />
          </div>
        )}
      </div>
    );
  }
}

TabsWithScroll.defaultProps = {
  active: null,
  fullWidth: false,
  RouterLink: null,
  segmented: false,
  setActive: () => {},
  withoutBorderBottom: false,
};

export default TabsWithScroll;
